import type { LiteratureDto } from '~/utils/solr/literature'
import { getLastName } from '#shared/utils/solr-facet'
import { sortByPrimaryIdentity, stableSort } from '~/utils/catalogue-relevance'
import { parseToChronology } from '~/utils/sort'
import { compareLocaleNorwegian, normalizeSearchText } from '~/utils/string'

export type EditionFamilyMap = ReadonlyMap<string, string>

function parseEdition(item: LiteratureDto): number | undefined {
  const edition = Number(item.edition)
  return Number.isSafeInteger(edition) && edition > 0 ? edition : undefined
}

function normalizeFamilyTitle(title: string | null | undefined): string {
  return title?.normalize('NFKC').toLowerCase().trim().replace(/\s+/g, ' ') ?? ''
}

function collectComponent(start: string, adjacency: Map<string, Set<string>>, seen: Set<string>) {
  const component: string[] = []
  const pending = [start]
  while (pending.length) {
    const id = pending.pop()!
    if (seen.has(id)) {
      continue
    }
    seen.add(id)
    component.push(id)
    pending.push(...(adjacency.get(id) ?? []))
  }
  return component
}

function isSafeComponent(
  ids: string[],
  booksById: Map<string, LiteratureDto>,
  predecessorBySuccessor: Map<string, string>,
): boolean {
  if (ids.length < 2) {
    return false
  }

  const books = ids.map((id) => booksById.get(id)!)
  const titles = new Set(books.map((book) => normalizeFamilyTitle(book.rawTitle)))
  const editions = new Map(books.map((book) => [book.id, parseEdition(book)]))
  if (
    titles.size !== 1 ||
    titles.has('') ||
    [...editions.values()].some((value) => value === undefined)
  ) {
    return false
  }

  const edges = ids.flatMap((id) => {
    const predecessorId = predecessorBySuccessor.get(id)
    return predecessorId && booksById.has(predecessorId) ? [[id, predecessorId] as const] : []
  })
  const successorCounts = new Map<string, number>()
  for (const [successorId, predecessorId] of edges) {
    successorCounts.set(predecessorId, (successorCounts.get(predecessorId) ?? 0) + 1)
    if ((editions.get(predecessorId) ?? 0) >= (editions.get(successorId) ?? 0)) {
      return false
    }
  }
  if (edges.length !== ids.length - 1 || [...successorCounts.values()].some((count) => count > 1)) {
    return false
  }

  const currentBooks = books.filter((book) => book.lastEdition === true)
  const highestEdition = Math.max(...books.map((book) => editions.get(book.id)!))
  return (
    currentBooks.length <= 1 &&
    (!currentBooks[0] || editions.get(currentBooks[0].id) === highestEdition)
  )
}

export function buildEditionFamilyMap(items: readonly LiteratureDto[]): EditionFamilyMap {
  const books = items.filter((item) => item.nodeType === 'book')
  const idCounts = new Map<string, number>()
  books.forEach((book) => idCounts.set(book.id, (idCounts.get(book.id) ?? 0) + 1))
  const duplicateIds = new Set([...idCounts].filter(([, count]) => count > 1).map(([id]) => id))
  const booksById = new Map(
    books.filter((book) => !duplicateIds.has(book.id)).map((book) => [book.id, book]),
  )
  const adjacency = new Map([...booksById.keys()].map((id) => [id, new Set<string>()]))
  const predecessorBySuccessor = new Map<string, string>()

  for (const book of booksById.values()) {
    const predecessorId = book.previousEdition
    if (!predecessorId || !booksById.has(predecessorId)) {
      continue
    }
    predecessorBySuccessor.set(book.id, predecessorId)
    adjacency.get(book.id)!.add(predecessorId)
    adjacency.get(predecessorId)!.add(book.id)
  }

  const families = new Map<string, string>()
  const seen = new Set<string>()
  for (const id of booksById.keys()) {
    const component = collectComponent(id, adjacency, seen)
    if (!isSafeComponent(component, booksById, predecessorBySuccessor)) {
      continue
    }
    const familyId = [...component].sort()[0]!
    component.forEach((memberId) => families.set(memberId, familyId))
  }
  return families
}

function compareTitle(left: LiteratureDto, right: LiteratureDto): number {
  return compareLocaleNorwegian(left.title.toLowerCase(), right.title.toLowerCase())
}

function compareChronology(left: LiteratureDto, right: LiteratureDto): number {
  const leftDate = parseToChronology(
    left.lastUpdatedByEditor || left.dateOfPublication || left.year,
  )
  const rightDate = parseToChronology(
    right.lastUpdatedByEditor || right.dateOfPublication || right.year,
  )
  return rightDate - leftDate
}

function compareAuthor(left: LiteratureDto, right: LiteratureDto): number {
  return compareLocaleNorwegian(
    getLastName(left.authors[0] || '').toLowerCase(),
    getLastName(right.authors[0] || '').toLowerCase(),
  )
}

function orderEditionFamilies(items: LiteratureDto[], families: EditionFamilyMap): LiteratureDto[] {
  const ordered = [...items]
  const positions = new Map<string, number[]>()
  ordered.forEach((item, index) => {
    const familyId = families.get(item.id)
    if (familyId) {
      positions.set(familyId, [...(positions.get(familyId) ?? []), index])
    }
  })
  for (const indexes of positions.values()) {
    const family = stableSort(
      indexes.map((index) => ordered[index]!),
      (left, right) => parseEdition(right)! - parseEdition(left)!,
    )
    indexes.forEach((index, familyIndex) => (ordered[index] = family[familyIndex]!))
  }
  return ordered
}

export function sortLiteratureCatalogue(
  items: readonly LiteratureDto[],
  query: string,
  sortValue: string,
  families: EditionFamilyMap,
): LiteratureDto[] {
  const hasQuery = Boolean(normalizeSearchText(query))
  if (sortValue === 'kronologisk' || (sortValue === 'relevans' && !hasQuery)) {
    return stableSort(items, compareChronology)
  }
  if (sortValue === 'forfatter') {
    return stableSort(items, compareAuthor)
  }

  const sorted =
    sortValue === 'relevans'
      ? sortByPrimaryIdentity(items, query, (item) => [item.title], compareTitle)
      : stableSort(items, compareTitle)
  return orderEditionFamilies(sorted, families)
}
